import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      setError('Blog post not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#4A5D54]">Loading...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <h1 className="text-4xl font-bold text-[#0A3D2E] mb-4">Post Not Found</h1>
        <Link to="/blog" className="text-[#0A3D2E] hover:text-[#CDFF00]">
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <article data-testid="blog-post-article" className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="back-to-blog-link"
            className="inline-flex items-center text-[#4A5D54] hover:text-[#0A3D2E] mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {post.image_url && (
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full aspect-video object-cover rounded-2xl mb-8"
              />
            )}

            <div className="flex items-center space-x-4 text-sm text-[#4A5D54] mb-6">
              <span className="flex items-center">
                <Calendar size={16} className="mr-2" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center">
                <User size={16} className="mr-2" />
                {post.author}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0A3D2E] mb-6 font-['Outfit']">
              {post.title}
            </h1>

            <div
              className="prose prose-lg max-w-none prose-headings:font-['Outfit'] prose-headings:text-[#0A3D2E] prose-a:text-[#0A3D2E] prose-a:no-underline hover:prose-a:text-[#CDFF00]"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
            />
          </motion.div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
